import $ from 'jquery';
import Swiper, { Scrollbar, Lazy } from 'swiper';
import { debounce } from '@github/mini-throttle';
import { movePromoTiles, resetElementPositions, savePositions, DESKTOP_GRID_COLUMNS, MOBILE_GRID_COLUMNS, isMobile as isMobileForRoot, getRootFromAny } from './promo-tiles-utils';
import { ensureGlobalBindings, subscribe } from './promo-tiles-core';
Swiper.use([
    Lazy,
    Scrollbar
]);
const SWIPER_SLIDE_GAP = 16;
const RESIZE_DEBOUNCE_TIME = 250;
const SWIPER_UPDATE_DELAY = 50;
const DESKTOP_SELECTOR = '.js-promo-tiles-slider-desktop';
const MOBILE_SELECTOR = '.js-promo-tiles-slider-mobile';
const MOBILE_CONTAINER_SELECTOR = '.js-promo-tiles-slider-mobile-container';
const PRODUCT_GRID_SELECTOR = '.js-product-grid > li';
const PRODUCT_GRID_ANCHOR = '[data-id]' // UL has data-id="{{ section.id }}"
;
const tilesSwipersByRoot = new WeakMap();
const registry = new Map();
const $within = (root, sel)=>$(root).find(sel);
const initPromoTilesDesktop = (root)=>{
    const sections = $within(root, DESKTOP_SELECTOR);
    sections.find('.js-promo-tile-image').off('mouseenter mouseleave');
    sections.each((_, sEl)=>{
        const $section = $(sEl);
        const $tiles = $section.find('.js-promo-tile');
        if (!$tiles.length) return;
        $tiles.find('.js-promo-tile-image').each((_i, el)=>{
            $(el).on('mouseenter', function() {
                const $tile = $(this).closest('.js-promo-tile');
                $tiles.not($tile).removeClass('tile--active');
                $tile.addClass('tile--active');
            });
            $(el).on('mouseleave', function() {
                $tiles.find('.promo-tiles__text').removeClass('promo-tile--active');
            });
        });
    });
};
const initPromoTilesMobile = (root)=>{
    var _tilesSwipersByRoot_get;
    const swipers = (_tilesSwipersByRoot_get = tilesSwipersByRoot.get(root)) !== null && _tilesSwipersByRoot_get !== void 0 ? _tilesSwipersByRoot_get : new Map();
    swipers.forEach((s)=>s.destroy(true, true));
    swipers.clear();
    const sections = $within(root, MOBILE_SELECTOR);
    sections.each((_, sEl)=>{
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const containerEl = $(sEl).find(`${MOBILE_CONTAINER_SELECTOR} .swiper-container`).first().get(0);
        if (!containerEl) return;
        const options = {
            autoHeight: true,
            direction: 'horizontal',
            loop: false,
            slidesPerView: 1.53,
            spaceBetween: SWIPER_SLIDE_GAP,
            watchSlidesProgress: true,
            slidesOffsetAfter: SWIPER_SLIDE_GAP,
            slidesOffsetBefore: 0,
            scrollbar: {
                el: '.js-promo-tiles-slider-mobile .swiper-scrollbar',
                draggable: true,
                hide: false
            }
        };
        const swiper = new Swiper(containerEl, options);
        swipers.set(containerEl, swiper);
    });
    tilesSwipersByRoot.set(root, swipers);
};
const setupForContainer = async (root)=>{
    if (registry.has(root)) return;
    const saveInitialState = async ()=>{
        const $desktop = $within(root, DESKTOP_SELECTOR);
        const $mobile = $within(root, MOBILE_SELECTOR);
        const dh = $desktop.is(':hidden');
        if (dh) $desktop.show();
        const mh = $mobile.is(':hidden');
        if (mh) $mobile.show();
        savePositions('desktop-tiles-slider', DESKTOP_SELECTOR, root);
        savePositions('mobile-tiles-slider', MOBILE_SELECTOR, root);
        if (dh) $desktop.hide();
        if (mh) $mobile.hide();
    };
    const applyLayout = async ()=>{
        resetElementPositions(DESKTOP_SELECTOR, root);
        resetElementPositions(MOBILE_SELECTOR, root);
        if (isMobileForRoot(root)) {
            $within(root, DESKTOP_SELECTOR).hide();
            $within(root, MOBILE_SELECTOR).show();
            initPromoTilesMobile(root);
            movePromoTiles(MOBILE_SELECTOR, PRODUCT_GRID_SELECTOR, MOBILE_GRID_COLUMNS, root);
            await new Promise((r)=>{
                return setTimeout(()=>{
                    var _tilesSwipersByRoot_get;
                    (_tilesSwipersByRoot_get = tilesSwipersByRoot.get(root)) === null || _tilesSwipersByRoot_get === void 0 ? void 0 : _tilesSwipersByRoot_get.forEach((sw)=>sw.update());
                    r(null);
                }, SWIPER_UPDATE_DELAY);
            });
        } else {
            $within(root, MOBILE_SELECTOR).hide();
            $within(root, DESKTOP_SELECTOR).show();
            movePromoTiles(DESKTOP_SELECTOR, PRODUCT_GRID_SELECTOR, DESKTOP_GRID_COLUMNS, root);
            initPromoTilesDesktop(root);
        }
    };
    await saveInitialState();
    await applyLayout();
    const onResize = debounce(()=>{
        void applyLayout();
    }, RESIZE_DEBOUNCE_TIME);
    window.addEventListener('resize', onResize);
    // NEW: subscribe to shared scheduler (fires once per frame per root)
    const unsubscribe = subscribe(root, ()=>{
        void applyLayout();
    });
    registry.set(root, {
        cleanup: ()=>{
            window.removeEventListener('resize', onResize);
            const swipers = tilesSwipersByRoot.get(root);
            swipers === null || swipers === void 0 ? void 0 : swipers.forEach((sw)=>sw.destroy(true, true));
            swipers === null || swipers === void 0 ? void 0 : swipers.clear();
            tilesSwipersByRoot.delete(root);
            unsubscribe() // NEW
            ;
        }
    });
};
const cleanupForContainer = (root)=>{
    const entry = registry.get(root);
    if (!entry) return;
    entry.cleanup();
    registry.delete(root);
};
export const init = async ()=>{
    // NEW: bind the shared global listener once
    ensureGlobalBindings({
        getRootFromAny
    });
    // Editor events — ALWAYS bind (even after DOM is loaded)
    document.addEventListener('shopify:section:load', (e)=>{
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const target = e.target;
        void setupForContainer(getRootFromAny(target));
    });
    document.addEventListener('shopify:section:unload', (e)=>{
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const target = e.target;
        cleanupForContainer(getRootFromAny(target));
    });
    // REMOVED: local 'collection:rendered' listener (now handled by core)
    // document.addEventListener('collection:rendered', ...)
    // Initial boot for currently-rendered sections
    const boot = ()=>{
        document.querySelectorAll(PRODUCT_GRID_ANCHOR).forEach((anchor)=>{
            const root = getRootFromAny(anchor);
            void setupForContainer(root);
        });
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
    // optional global cleanup
    return ()=>{
        document.querySelectorAll(PRODUCT_GRID_ANCHOR).forEach((anchor)=>{
            cleanupForContainer(getRootFromAny(anchor));
        });
    };
};
